import type { Asset, Draft, Surface, PanelProgress } from './types';
import {surfacePaintRegions} from './surfaceRegions';
import { isFrontierPanel, isPaintableSurface } from './surfaceRoles';
import {compileSurfaceMask,compiledMaskCell,compiledMaskContains,maskCellIntersectsConvex,type CompiledSurfaceMask} from './surfaceGeometry';
export const CELL_SIZE = 4;
export const ALPHA_THRESHOLD = 32;
export { DAILY_INK, INK_AREA, GUEST_INK } from './economy';
import { INK_AREA } from './economy';
// V8 adds paint-order compatibility; saved V1/V2 geometry stays immutable.
export const GEOMETRY_VERSION = 10;
export const PANEL_TARGET_PERCENT = 50;
export const PANEL_MIN_COVERAGE_PERCENT = 5;
export const DEFAULT_EDGE = 96;
export const MIN_EDGE = 24;
// Source images are at most 2048px along one axis and at least one pixel along
// the other. This bounds unknown-asset preview geometry without a small art cap.
export const MAX_SOURCE_EDGE = 2048;
// Allow close-up crops and large stamps while rendering, cost and coverage stay
// clipped to the selected physical surface. This scales the aspect-aware cover fit.
export const MAX_COVER_SCALE = 4;
export type PlacementGeometry = Pick<Draft, 'x' | 'y' | 'width' | 'height' | 'rotation' | 'opacity'>;
export interface Point { x: number; y: number }
export interface Bounds { x: number; y: number; width: number; height: number }
/** Clockwise degrees around the artwork centre; old placements have zero rotation. */
export function normalizeRotation(rotation?: number): number {
  if (rotation === undefined) return 0;
  if (typeof rotation !== 'number' || !Number.isFinite(rotation)) return NaN;
  const normalized = ((rotation % 360) + 540) % 360 - 180;
  return normalized === 0 ? 0 : normalized;
}
/** Layer opacity multiplies source alpha; old placements retain full opacity. */
export function normalizeOpacity(opacity?: number): number {
  if (opacity === undefined) return 1;
  return typeof opacity === 'number' && Number.isFinite(opacity) && opacity >= 0 && opacity <= 1 ? opacity : NaN;
}
function trig(rotation?: number) {
  const angle = normalizeRotation(rotation), radians = angle * Math.PI / 180;
  // Exact quarter turns avoid classifying cell centres differently through roundoff.
  return { cos: angle % 90 === 0 ? (angle === 0 ? 1 : angle === -180 ? -1 : 0) : Math.cos(radians), sin: angle % 90 === 0 ? (angle === 90 ? 1 : angle === -90 ? -1 : 0) : Math.sin(radians) };
}
export function placementCorners(draft: PlacementGeometry): Point[] {
  const { cos, sin } = trig(draft.rotation), cx = draft.x + draft.width / 2, cy = draft.y + draft.height / 2;
  return [[-1,-1],[1,-1],[1,1],[-1,1]].map(([sx,sy]) => { const dx=sx*draft.width/2, dy=sy*draft.height/2; return { x:cx+dx*cos-dy*sin, y:cy+dx*sin+dy*cos }; });
}
export function placementBounds(draft: PlacementGeometry): Bounds {
  const points=placementCorners(draft), xs=points.map(point=>point.x), ys=points.map(point=>point.y);
  const x=Math.min(...xs), y=Math.min(...ys); return { x,y,width:Math.max(...xs)-x,height:Math.max(...ys)-y };
}
export function gridSize(surface: Surface) { return { columns: Math.ceil(surface.width / CELL_SIZE), rows: Math.ceil(surface.height / CELL_SIZE) }; }
const contourEligibility=new WeakMap<CompiledSurfaceMask,number[]>();
function contourEligibleCells(surface:Surface):number[] {
  const mask=compileSurfaceMask(surface),cached=contourEligibility.get(mask);if(cached)return cached;
  const {columns,rows}=gridSize(surface),cells:number[]=[];
  for(let row=0;row<rows;row++)for(let column=0;column<columns;column++)if(compiledMaskCell(mask,{x:column*CELL_SIZE,y:row*CELL_SIZE,width:Math.min(CELL_SIZE,surface.width-column*CELL_SIZE),height:Math.min(CELL_SIZE,surface.height-row*CELL_SIZE)}).polygons.length)cells.push(row*columns+column);
  contourEligibility.set(mask,cells);return cells;
}
export function eligibleCellIndices(surface: Surface): number[] {
  if((surface.contours!==undefined||surface.panelReservations!==undefined||surface.exteriorOcclusions!==undefined||surface.exteriorClip!==undefined))return isPaintableSurface(surface)?[...contourEligibleCells(surface)]:[];
  const {columns,rows}=gridSize(surface),regions=surfacePaintRegions(surface),cells:number[]=[];
  for(let row=0;row<rows;row++) for(let column=0;column<columns;column++) {
    const x=column*CELL_SIZE,y=row*CELL_SIZE;
    if(regions.some(r=>x<r.x+r.width && x+CELL_SIZE>r.x && y<r.y+r.height && y+CELL_SIZE>r.y))cells.push(row*columns+column);
  }
  return cells;
}
function eligibleCellUsingMask(surface: Surface,cell:number,getMask?:()=>CompiledSurfaceMask): boolean {
  const {columns,rows}=gridSize(surface);if(!Number.isSafeInteger(cell)||cell<0||cell>=columns*rows)return false;
  if(!isPaintableSurface(surface))return false;
  if((surface.contours!==undefined||surface.panelReservations!==undefined||surface.exteriorOcclusions!==undefined||surface.exteriorClip!==undefined)){const x=(cell%columns)*CELL_SIZE,y=Math.floor(cell/columns)*CELL_SIZE;return !!compiledMaskCell(getMask?getMask():compileSurfaceMask(surface),{x,y,width:Math.min(CELL_SIZE,surface.width-x),height:Math.min(CELL_SIZE,surface.height-y)}).polygons.length;}
  if(surface.regions===undefined)return true;
  const x=(cell%columns)*CELL_SIZE,y=Math.floor(cell/columns)*CELL_SIZE;
  return surfacePaintRegions(surface).some(r=>x<r.x+r.width && x+CELL_SIZE>r.x && y<r.y+r.height && y+CELL_SIZE>r.y);
}
export function isEligibleCell(surface:Surface,cell:number):boolean {return eligibleCellUsingMask(surface,cell);}
/** Reuse only within one synchronous operation over unchanged geometry. The
 * ordinary public checker still observes mutable inputs on every call. */
export function operationCellEligibility(surface:Surface):(cell:number)=>boolean {
  let compiled:CompiledSurfaceMask|undefined;
  const getMask=()=>compiled??=compileSurfaceMask(surface);
  return cell=>eligibleCellUsingMask(surface,cell,getMask);
}
export function eligibleCells(surface: Surface): number { const { columns, rows } = gridSize(surface); return !isPaintableSurface(surface)?0:(surface.contours!==undefined||surface.panelReservations!==undefined||surface.exteriorOcclusions!==undefined||surface.exteriorClip!==undefined)?contourEligibleCells(surface).length:surface.regions===undefined?columns*rows:eligibleCellIndices(surface).length; }
export function maxEdge(surface: Surface, asset: Asset): number {
  const ratio=asset.width/asset.height, unitW=ratio>=1?1:ratio, unitH=ratio>=1?1/ratio:1;
  return MAX_COVER_SCALE*Math.max(surface.width/unitW,surface.height/unitH);
}
export function fitDraft(surface: Surface, asset: Asset, x: number, y: number, edge?: number, rotation = 0, opacity = 1, paintOrder: Draft['paintOrder'] = 'over'): Draft {
  // Initial placement is modest on small surfaces; resizing is limited by artwork,
  // not by the wall. Selection bounds are free to extend beyond every edge.
  const size=Math.min(maxEdge(surface,asset),Math.max(MIN_EDGE,edge ?? Math.min(DEFAULT_EDGE,surface.width,surface.height)));
  const ratio=asset.width/asset.height, width=ratio>=1?size:size*ratio, height=ratio>=1?size/ratio:size;
  const draft: Draft={surfaceId:surface.id,assetId:asset.id,x:0,y:0,width,height,rotation:normalizeRotation(rotation),opacity:normalizeOpacity(opacity),paintOrder};
  const bounds=placementBounds(draft), halfX=bounds.width/2, halfY=bounds.height/2;
  draft.x=Math.max(CELL_SIZE/2-width/2-halfX+1e-6,Math.min(surface.width-CELL_SIZE/2-width/2+halfX-1e-6,x));
  draft.y=Math.max(CELL_SIZE/2-height/2-halfY+1e-6,Math.min(surface.height-CELL_SIZE/2-height/2+halfY-1e-6,y));
  return draft;
}
export function validateGeometry(surface: Surface, draft: PlacementGeometry): string | null {
  if(!isPaintableSurface(surface))return 'Recorded exclusions cannot receive artwork.';
  const {x,y,width,height}=draft, rotation=normalizeRotation(draft.rotation);
  if (![x,y,width,height,rotation].every(Number.isFinite)) return 'Placement coordinates and rotation must be finite.';
  if (!Number.isFinite(normalizeOpacity(draft.opacity))) return 'Artwork opacity must be finite and between 0 and 1.';
  if (width<=0 || height<=0) return 'Artwork dimensions must be positive.';
  const bound = Math.max(surface.width,surface.height)*MAX_SOURCE_EDGE*MAX_COVER_SCALE;
  if (Math.max(width,height)>bound+1e-6) return 'This artwork exceeds the selected surface size limit.';
  if (Math.max(width,height)<MIN_EDGE-1e-6) return 'This artwork is too small.';
  const margin=Math.hypot(width,height);
  if (x < -margin || y < -margin || x > surface.width+margin || y > surface.height+margin) return 'Keep visible artwork on the selected surface.';
  const bounds=placementBounds(draft);
  if (bounds.x+ bounds.width<=0 || bounds.y+bounds.height<=0 || bounds.x>=surface.width || bounds.y>=surface.height) return 'Keep visible artwork on the selected surface.';
  return null;
}
function validateDraftStructure(surface:Surface,asset:Asset,draft:Draft):string|null {
  const invalid=validateGeometry(surface,draft); if(invalid) return invalid;
  if (Math.abs(draft.width/draft.height-asset.width/asset.height)>1e-5) return 'Artwork must keep its original aspect ratio.';
  if (Math.max(draft.width,draft.height)>maxEdge(surface,asset)+1e-6) return 'This artwork exceeds the selected surface size limit.';
  return null;
}
/** Preserve validation order while returning the exact footprint already used
 * to reject empty artwork. No caller-supplied cells or price are trusted. */
export function validateDraftFootprint(surface:Surface,asset:Asset,draft:Draft):
  {ok:false;error:string}|{ok:true;painted:ReturnType<typeof footprint>} {
  const invalid=validateDraftStructure(surface,asset,draft);
  if(invalid)return {ok:false,error:invalid};
  const painted=footprint(surface,asset,draft);
  return painted.cells.length?{ok:true,painted}:{ok:false,error:'This placement has no visible painted area.'};
}
export function validateDraft(surface: Surface, asset: Asset, draft: Draft): string | null {
  const checked=validateDraftFootprint(surface,asset,draft);
  return checked.ok?null:checked.error;
}
/** Drawing detail uses positive-area intersections, rather than only cell
 * centres. Each opaque mask pixel is a rotated rectangle. The separating-axis
 * test against a clipped grid cell checks both rectangles' axes; touching at a
 * point or line alone does not count. Occupied cells still represent 16 units
 * of area, so thin strokes are charged at canonical grid precision. */
function drawingCells(surface: Surface, asset: Asset, draft: PlacementGeometry): number[] {
  const { columns, rows } = gridSize(surface), occupied = new Uint8Array(columns * rows);
  const { cos, sin } = trig(draft.rotation), ac = Math.abs(cos), as = Math.abs(sin);
  const stepX = draft.width / asset.mask.width, stepY = draft.height / asset.mask.height;
  const halfX = stepX / 2, halfY = stepY / 2;
  const radiusX = ac * halfX + as * halfY, radiusY = as * halfX + ac * halfY;
  const centreX = draft.x + draft.width / 2, centreY = draft.y + draft.height / 2;
  const opacity = normalizeOpacity(draft.opacity);
  const contour=(surface.contours!==undefined||surface.panelReservations!==undefined||surface.exteriorOcclusions!==undefined||surface.exteriorClip!==undefined)?compileSurfaceMask(surface):undefined,regions=contour?[]:surfacePaintRegions(surface);
  let count = 0;
  for (let my = 0; my < asset.mask.height; my++) for (let mx = 0; mx < asset.mask.width; mx++) {
    if (!(asset.mask.alpha[my * asset.mask.width + mx] * opacity >= ALPHA_THRESHOLD)) continue;
    const localX = (mx + 0.5) * stepX - draft.width / 2, localY = (my + 0.5) * stepY - draft.height / 2;
    const pixelX = centreX + localX * cos - localY * sin, pixelY = centreY + localX * sin + localY * cos;
    if (pixelX + radiusX <= 0 || pixelY + radiusY <= 0 || pixelX - radiusX >= surface.width || pixelY - radiusY >= surface.height) continue;
    // Bound work to this pixel's AABB inside the chosen wall, even for extreme
    // aspect ratios and overhang. Fully covered cells are never tested twice.
    const startX = Math.max(0, Math.floor((pixelX - radiusX) / CELL_SIZE)), endX = Math.min(columns, Math.ceil((pixelX + radiusX) / CELL_SIZE));
    const startY = Math.max(0, Math.floor((pixelY - radiusY) / CELL_SIZE)), endY = Math.min(rows, Math.ceil((pixelY + radiusY) / CELL_SIZE));
    let pixelCorners:[number,number][]|undefined;
    for (let row = startY; row < endY; row++) for (let column = startX; column < endX; column++) {
      const cell = row * columns + column;
      if (occupied[cell]) continue;
      const left = column * CELL_SIZE, top = row * CELL_SIZE;
      // Intersect the mask pixel with cell AND eligible face, not merely a cell
      // that touches the face. Paint entirely inside a window hole costs zero.
      let intersects=false;
      const curveCell=contour?compiledMaskCell(contour,{x:left,y:top,width:Math.min(CELL_SIZE,surface.width-left),height:Math.min(CELL_SIZE,surface.height-top)}):undefined;
      if(curveCell&&!curveCell.polygons.length)continue;
      if(curveCell&&!curveCell.full){
        pixelCorners??=[[-halfX,-halfY],[halfX,-halfY],[halfX,halfY],[-halfX,halfY]].map(([x,y]):[number,number]=>[pixelX+x*cos-y*sin,pixelY+x*sin+y*cos]);
        intersects=maskCellIntersectsConvex(curveCell,pixelCorners);
      }else for(const region of curveCell?[curveCell.region]:regions){
      const x=Math.max(left,region.x),y=Math.max(top,region.y),right=Math.min(surface.width,left+CELL_SIZE,region.x+region.width),bottom=Math.min(surface.height,top+CELL_SIZE,region.y+region.height);
      if(right<=x || bottom<=y)continue;
      const cellHalfX=(right-x)/2,cellHalfY=(bottom-y)/2;
      const dx = x + cellHalfX - pixelX, dy = y + cellHalfY - pixelY;
      // Strict overlap rejects zero-area edge contact, with a small numerical
      // tolerance for exact quarter turns and large finite overhangs.
      const epsilon = 1e-9;
      if (Math.abs(dx) + epsilon >= radiusX + cellHalfX || Math.abs(dy) + epsilon >= radiusY + cellHalfY) continue;
      if (Math.abs(dx * cos + dy * sin) + epsilon >= halfX + cellHalfX * ac + cellHalfY * as) continue;
      if (Math.abs(-dx * sin + dy * cos) + epsilon >= halfY + cellHalfX * as + cellHalfY * ac) continue;
      intersects=true;break;
      }
      if(!intersects)continue;
      occupied[cell] = 1;
      if (++count === occupied.length) return Array.from({ length: count }, (_, index) => index);
    }
  }
  const cells: number[] = [];
  for (let cell = 0; cell < occupied.length; cell++) if (occupied[cell]) cells.push(cell);
  return cells;
}
export function footprint(surface: Surface, asset: Asset, draft: PlacementGeometry): { cells: number[]; area: number; cost: number } {
  const {columns,rows}=gridSize(surface), cells: number[]=[];
  if(!isPaintableSurface(surface))return {cells,area:0,cost:0};
  if (![draft.x,draft.y,draft.width,draft.height,normalizeRotation(draft.rotation)].every(Number.isFinite) || draft.width<=0 || draft.height<=0) return {cells,area:0,cost:0};
  const opacity = normalizeOpacity(draft.opacity);
  if (!Number.isFinite(opacity) || opacity === 0) return {cells,area:0,cost:0};
  if (asset.kind === 'drawing') {
    const cells = drawingCells(surface, asset, draft), area = cells.length * CELL_SIZE * CELL_SIZE;
    return { cells, area, cost: cells.length ? Math.max(1, Math.ceil(area / INK_AREA)) : 0 };
  }
  const bounds=placementBounds(draft), {cos,sin}=trig(draft.rotation), zero=normalizeRotation(draft.rotation)===0;
  const contour=(surface.contours!==undefined||surface.panelReservations!==undefined||surface.exteriorOcclusions!==undefined||surface.exteriorClip!==undefined)?compileSurfaceMask(surface):undefined,regions=contour||surface.regions===undefined?undefined:surfacePaintRegions(surface);
  const cx=draft.x+draft.width/2, cy=draft.y+draft.height/2;
  const startX=Math.max(0,Math.floor(bounds.x/CELL_SIZE)), endX=Math.min(columns,Math.ceil((bounds.x+bounds.width)/CELL_SIZE));
  const startY=Math.max(0,Math.floor(bounds.y/CELL_SIZE)), endY=Math.min(rows,Math.ceil((bounds.y+bounds.height)/CELL_SIZE));
  for(let row=startY;row<endY;row++) for(let column=startX;column<endX;column++) {
    const px=column*CELL_SIZE+CELL_SIZE/2, py=row*CELL_SIZE+CELL_SIZE/2;
    if(px>=surface.width || py>=surface.height) continue;
    if(contour&&!compiledMaskContains(contour,px,py))continue;
    if(regions && !regions.some(r=>px>=r.x && py>=r.y && px<r.x+r.width && py<r.y+r.height))continue;
    const dx=px-cx, dy=py-cy;
    // Preserve the legacy arithmetic exactly at zero degrees.
    const u=zero?(px-draft.x)/draft.width:(dx*cos+dy*sin)/draft.width+0.5;
    const v=zero?(py-draft.y)/draft.height:(-dx*sin+dy*cos)/draft.height+0.5;
    if(u<0 || v<0 || u>=1 || v>=1) continue;
    const mx=Math.min(asset.mask.width-1,Math.floor(u*asset.mask.width)), my=Math.min(asset.mask.height-1,Math.floor(v*asset.mask.height));
    if(asset.mask.alpha[my*asset.mask.width+mx]*opacity>=ALPHA_THRESHOLD) cells.push(row*columns+column);
  }
  const area=cells.length*CELL_SIZE*CELL_SIZE;
  return {cells,area,cost:cells.length?Math.max(1,Math.ceil(area/INK_AREA)):0};
}
/** Exact integer comparisons; rounded UI percentages never authorize an opening. */
export function qualifiesPanel(coveredCells: number, eligible: number): boolean {
  return eligible > 0 && coveredCells * 100 >= eligible * PANEL_MIN_COVERAGE_PERCENT;
}
export function reachesPanelUnlock(qualifiedPanels: number, totalPanels: number): boolean {
  return totalPanels > 0 && qualifiedPanels * 100 >= totalPanels * PANEL_TARGET_PERCENT;
}

/** Progress for an explicit authored surface set; runtime/provider adoption is
 * separate. Outside paint has normal accounting but never qualifies a panel. */
export function panelProgressForSurfaces(blockId:number,surfaces:readonly Surface[],counts:ReadonlyMap<string,number>):PanelProgress {
  const panels=surfaces.filter(isFrontierPanel),qualifiedPanels=panels.filter(surface=>qualifiesPanel(counts.get(surface.id)??0,eligibleCells(surface))).length;
  return {blockId,qualifiedPanels,totalPanels:panels.length,ratio:panels.length?qualifiedPanels/panels.length:0};
}
